import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { api, MASTERCLASS_BANNER, SQUARE_PAYMENT_LINK, REGISTRATION_FEE } from "@/lib/data";

const BENEFITS = [
  "AI, Cryptocurrency & Digital Assets",
  "Robotics, Space Exploration & Emerging Tech",
  "Building an investment portfolio for where the world is going",
  "Creating multiple streams of income",
  "Positioning yourself for generational wealth",
];

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await api.post("/register", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
      });
      setRegistered(true);
      toast.success("You're registered! Complete your payment to secure your seat.");
      window.open(SQUARE_PAYMENT_LINK, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 md:pt-28 pb-24 bg-charcoal min-h-screen text-sand" data-testid="register-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl overflow-hidden shadow-2xl border border-white/10"
        >
          <img
            src={MASTERCLASS_BANNER}
            alt="The Financial Masterclass — The Future Is Here"
            data-testid="register-banner"
            className="w-full object-cover"
          />
        </motion.div>

        <div className="mt-14 grid md:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:col-span-7"
          >
            <p className="overline text-terracotta inline-flex items-center gap-2">
              <Sparkles size={15} /> The Financial Masterclass
            </p>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              The Future Is Here: Prepare Yourself for the{" "}
              <span className="italic text-terracotta">Next Shift of Wealth</span>
            </h1>

            <div className="mt-8 space-y-5 font-body text-sand/80 leading-relaxed">
              <p>
                AI. Cryptocurrency. Digital Assets. Robotics. Space Exploration. Emerging
                Technology. The world is changing — and with every major technological
                shift, new industries, new companies, new careers, and new opportunities
                for wealth creation emerge.
              </p>
              <p className="font-heading italic text-2xl text-sand">
                The question is: Will you be positioned for it?
              </p>
              <p>
                Money is moving all around you. Companies are being built. New technologies
                are transforming entire industries. Investors are searching for the
                businesses that could dominate the next decade. But are you positioned to
                participate — or are you watching the next wealth cycle happen without you?
              </p>
              <p>
                If you're tired of working a dead-end job… if you're tired of living
                paycheck to paycheck… if you dream about traveling without worrying about
                what the trip costs… if you want to build an investment portfolio, own
                assets, create multiple streams of income, retire earlier, or potentially
                build millionaire-level wealth…
              </p>
              <p className="font-heading text-2xl text-terracotta not-italic">
                The Financial Masterclass was created for you.
              </p>
            </div>

            <div className="mt-10">
              <p className="overline text-terracotta">What you'll learn</p>
              <ul className="mt-4 space-y-3">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 font-body text-sand/90">
                    <CheckCircle2 size={20} className="text-terracotta shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="md:col-span-5"
          >
            <div className="sticky top-28 rounded-3xl bg-white text-ink p-8 shadow-2xl">
              <p className="overline text-terracotta">Reserve Your Seat</p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-heading text-5xl text-ink">${REGISTRATION_FEE}</span>
                <span className="font-body text-ink-soft">registration fee</span>
              </div>
              <p className="mt-2 font-body text-sm text-ink-soft">
                Registration is limited — only 30 slots available.
              </p>

              {registered ? (
                <div className="mt-8 text-center" data-testid="register-success">
                  <CheckCircle2 size={44} className="text-forest mx-auto" />
                  <h3 className="mt-4 font-heading text-2xl text-ink">You're on the list!</h3>
                  <p className="mt-2 font-body text-ink-soft">
                    If the payment window didn't open, click below to complete your ${REGISTRATION_FEE} registration.
                  </p>
                  <a
                    href={SQUARE_PAYMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="register-pay-link"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-4 font-medium text-white transition-all hover:bg-terracotta-hover"
                  >
                    Complete Payment <ArrowRight size={18} />
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                  <div>
                    <label className="overline text-ink-soft">Full Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="register-name"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                  </div>
                  <div>
                    <label className="overline text-ink-soft">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="register-email"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                  </div>
                  <div>
                    <label className="overline text-ink-soft">Phone (optional)</label>
                    <input
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="register-phone"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="register-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-4 font-medium text-white transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-forest"
                  >
                    {submitting ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>Register &amp; Pay ${REGISTRATION_FEE} <ArrowRight size={18} /></>
                    )}
                  </button>
                  <p className="text-center font-body text-xs text-ink-soft">
                    You'll be taken to our secure Square checkout to complete payment.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
